import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import type { ReviewFilters, ReviewStatus, ReviewSource, ReviewType, ReviewContentType, ReviewRatingFilter, ReviewTagsMode } from "../types";
import type { Tag } from "@/modules/tag/types";
import { getTagColorStyles } from "@/modules/tag/colors";

interface ReviewFiltersProps {
	filters: ReviewFilters;
	onChange: (filters: ReviewFilters) => void;
	tags?: Tag[];
	className?: string;
	children?: React.ReactNode;
	pendingCount?: number;
	totalCount?: number;
	isLoading?: boolean;
}

// Filter options
const STATUS_OPTIONS: { value: ReviewStatus; label: string }[] = [
	{ value: "approved", label: "Approved" },
	{ value: "pending", label: "Pending" },
	{ value: "draft", label: "Draft" },
];

// Quick filter options (subset for the toolbar)
const QUICK_STATUS_OPTIONS: { value: ReviewStatus | ""; label: string }[] = [
	{ value: "", label: "All" },
	{ value: "pending", label: "Pending" },
	{ value: "approved", label: "Approved" },
	{ value: "unapproved", label: "Unapproved" },
];

const SOURCE_OPTIONS: { value: ReviewSource; label: string }[] = [
	{ value: "form", label: "Form" },
	{ value: "manual", label: "Manual" },
	{ value: "import", label: "Import" },
	{ value: "google_reviews", label: "Google Reviews" },
	{ value: "capterra", label: "Capterra" },
	{ value: "twitter", label: "Twitter" },
	{ value: "linkedin", label: "LinkedIn" },
	{ value: "facebook", label: "Facebook" },
	{ value: "instagram", label: "Instagram" },
	{ value: "trustpilot", label: "Trustpilot" },
	{ value: "email", label: "Email" },
	{ value: "slack", label: "Slack" },
	{ value: "product-hunt", label: "Product Hunt" },
	{ value: "other", label: "Other" },
];

const TYPE_OPTIONS: { value: ReviewType | ""; label: string }[] = [
	{ value: "", label: "All" },
	{ value: "testimonial", label: "Testimonial" },
	{ value: "feedback", label: "Feedback" },
];

const CONTENT_TYPE_OPTIONS: { value: ReviewContentType; label: string }[] = [
	{ value: "text", label: "Text" },
	{ value: "video", label: "Video" },
	{ value: "audio", label: "Audio" },
];

const RATING_OPTIONS: { value: ReviewRatingFilter | ""; label: string }[] = [
	{ value: "", label: "All Ratings" },
	{ value: "with_rating", label: "With Rating" },
	{ value: "without_rating", label: "Without Rating" },
	{ value: "5_and_above", label: "5 Stars Only" },
	{ value: "4_and_above", label: "4+ Stars" },
	{ value: "3_and_above", label: "3+ Stars" },
	{ value: "2_and_above", label: "2+ Stars" },
];

const DATE_PRESET_OPTIONS = [
	{ value: "", label: "All Time" },
	{ value: "today", label: "Today" },
	{ value: "last_7_days", label: "Last 7 Days" },
	{ value: "last_30_days", label: "Last 30 Days" },
	{ value: "this_month", label: "This Month" },
	{ value: "last_month", label: "Last Month" },
	{ value: "this_year", label: "This Year" },
];

function getDateFilterValue(preset: string): string | undefined {
	const today = new Date();
	const formatDate = (date: Date) => date.toISOString().split("T")[0];

	switch (preset) {
		case "today":
			return `between:${formatDate(today)},${formatDate(today)}`;
		case "last_7_days": {
			const sevenDaysAgo = new Date(today);
			sevenDaysAgo.setDate(today.getDate() - 7);
			return `after:${formatDate(sevenDaysAgo)}`;
		}
		case "last_30_days": {
			const thirtyDaysAgo = new Date(today);
			thirtyDaysAgo.setDate(today.getDate() - 30);
			return `after:${formatDate(thirtyDaysAgo)}`;
		}
		case "this_month": {
			const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
			return `between:${formatDate(startOfMonth)},${formatDate(today)}`;
		}
		case "last_month": {
			const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
			return `between:${formatDate(startOfLastMonth)},${formatDate(endOfLastMonth)}`;
		}
		case "this_year": {
			const startOfYear = new Date(today.getFullYear(), 0, 1);
			return `between:${formatDate(startOfYear)},${formatDate(today)}`;
		}
		default:
			return undefined;
	}
}

function getPresetFromDateFilter(dateFilter?: string): string {
	if (!dateFilter) return "";
	for (const preset of DATE_PRESET_OPTIONS) {
		if (preset.value && getDateFilterValue(preset.value) === dateFilter) {
			return preset.value;
		}
	}
	return "";
}

// Section Icons
const SectionIcons = {
	status: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
		</svg>
	),
	type: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
			/>
		</svg>
	),
	format: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
			/>
		</svg>
	),
	source: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
			/>
		</svg>
	),
	rating: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
			/>
		</svg>
	),
	date: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</svg>
	),
	tags: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
			/>
		</svg>
	),
};

// Accordion Section Component
function FilterSection({
	title,
	icon,
	isOpen,
	isActive,
	onToggle,
	children,
}: {
	title: string;
	icon: React.ReactNode;
	isOpen: boolean;
	isActive: boolean;
	onToggle: () => void;
	children: React.ReactNode;
}) {
	return (
		<div className="border-b border-gray-100 last:border-b-0">
			<button type="button" onClick={onToggle} className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors">
				<div className="flex items-center gap-2.5">
					<span className="text-gray-400">{icon}</span>
					<span className="text-sm font-medium text-primary">{title}</span>
				</div>
				<div className="flex items-center gap-2">
					{isActive && <span className="w-2 h-2 rounded-full bg-gray-800" />}
					<svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</div>
			</button>
			{isOpen && <div className="px-4 pb-4">{children}</div>}
		</div>
	);
}

// Chip/Pill Option Component
function ChipOption({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selected ? "bg-brand text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
		>
			{children}
		</button>
	);
}

// Quick Status Filter (single-select tabs)
function QuickStatusFilter({ value, onChange, pendingCount }: { value: ReviewStatus | undefined; onChange: (value: ReviewStatus | undefined) => void; pendingCount?: number }) {
	return (
		<div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
			{QUICK_STATUS_OPTIONS.map((option) => {
				const isSelected = (option.value === "" && !value) || value === option.value;
				const showCount = option.value === "pending" && pendingCount !== undefined && pendingCount > 0;

				return (
					<button
						key={option.value}
						type="button"
						onClick={() => onChange(option.value === "" ? undefined : (option.value as ReviewStatus))}
						className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
							isSelected ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-primary"
						}`}
					>
						{option.label}
						{showCount && (
							<span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-amber-500 text-amber-50">{pendingCount}</span>
						)}
					</button>
				);
			})}
		</div>
	);
}

// Quick Type Filter (single-select tabs for testimonial/feedback)
function QuickTypeFilter({ value, onChange }: { value: ReviewType | undefined; onChange: (value: ReviewType | undefined) => void }) {
	return (
		<div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
			{TYPE_OPTIONS.map((option) => (
				<button
					key={option.value}
					type="button"
					onClick={() => onChange(option.value === "" ? undefined : (option.value as ReviewType))}
					className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
						(option.value === "" && !value) || value === option.value ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-primary"
					}`}
				>
					{option.label}
				</button>
			))}
		</div>
	);
}

// Get human-readable label for active filter
function getFilterLabel(key: string, value: string | string[], tags?: Tag[]): string {
	switch (key) {
		case "status":
			if (Array.isArray(value)) {
				const count = value.length;
				if (count === 1) {
					return STATUS_OPTIONS.find((o) => o.value === value[0])?.label || String(value[0]);
				}
				return `${count} statuses`;
			}
			return STATUS_OPTIONS.find((o) => o.value === value)?.label || String(value);
		case "source":
			if (Array.isArray(value)) {
				const count = value.length;
				if (count === 1) {
					return SOURCE_OPTIONS.find((o) => o.value === value[0])?.label || String(value[0]);
				}
				return `${count} sources`;
			}
			return SOURCE_OPTIONS.find((o) => o.value === value)?.label || String(value);
		case "type":
			return TYPE_OPTIONS.find((o) => o.value === value)?.label || String(value);
		case "content_type":
			if (Array.isArray(value)) {
				const count = value.length;
				if (count === 1) {
					return CONTENT_TYPE_OPTIONS.find((o) => o.value === value[0])?.label || String(value[0]);
				}
				return `${count} formats`;
			}
			return CONTENT_TYPE_OPTIONS.find((o) => o.value === value)?.label || String(value);
		case "rating":
			return RATING_OPTIONS.find((o) => o.value === value)?.label || String(value);
		case "date":
			const preset = getPresetFromDateFilter(String(value));
			return DATE_PRESET_OPTIONS.find((o) => o.value === preset)?.label || "Custom Date";
		case "tags":
			if (Array.isArray(value) && tags) {
				const count = value.length;
				return count === 1 ? tags.find((t) => t.id === value[0])?.label || "1 tag" : `${count} tags`;
			}
			return "Tags";
		default:
			return String(value);
	}
}

export default function ReviewFiltersComponent({ filters, onChange, tags = [], className = "", children, pendingCount, totalCount, isLoading }: ReviewFiltersProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [openSections, setOpenSections] = useState<Set<string>>(new Set(["content_type", "source"]));

	// Draft state for filters (only applied when "Apply Filters" is clicked)
	const [draftFilters, setDraftFilters] = useState<ReviewFilters>(filters);
	const [draftDatePreset, setDraftDatePreset] = useState(() => getPresetFromDateFilter(filters.date));

	// Sync draft state when popup opens
	const handleOpenChange = (open: boolean) => {
		if (open) {
			setDraftFilters(filters);
			setDraftDatePreset(getPresetFromDateFilter(filters.date));
		}
		setIsOpen(open);
	};

	const toggleSection = (section: string) => {
		setOpenSections((prev) => {
			const next = new Set(prev);
			if (next.has(section)) {
				next.delete(section);
			} else {
				next.add(section);
			}
			return next;
		});
	};

	// Update draft filters (not applied yet)
	const handleDraftFilterChange = <K extends keyof ReviewFilters>(key: K, value: ReviewFilters[K] | "") => {
		const newFilters = { ...draftFilters };
		if (value === "" || value === undefined) {
			delete newFilters[key];
		} else {
			newFilters[key] = value;
		}
		setDraftFilters(newFilters);
	};

	const handleDraftDatePresetChange = (preset: string) => {
		setDraftDatePreset(preset);
		const dateValue = getDateFilterValue(preset);
		handleDraftFilterChange("date", dateValue);
	};

	const handleTagToggle = (tagId: string) => {
		const currentTags = draftFilters.tags || [];
		const newTags = currentTags.includes(tagId) ? currentTags.filter((id) => id !== tagId) : [...currentTags, tagId];

		setDraftFilters((prev) => ({
			...prev,
			tags: newTags.length > 0 ? newTags : undefined,
		}));
	};

	const handleTagsModeChange = (mode: ReviewTagsMode) => {
		setDraftFilters((prev) => ({
			...prev,
			tags_mode: mode,
		}));
	};

	// Multi-select toggle for Source
	const handleSourceToggle = (value: ReviewSource) => {
		const current = draftFilters.source;
		const currentArray = Array.isArray(current) ? current : current ? [current] : [];

		const newArray = currentArray.includes(value) ? currentArray.filter((v) => v !== value) : [...currentArray, value];

		setDraftFilters((prev) => ({
			...prev,
			source: newArray.length > 0 ? (newArray.length === 1 ? newArray[0] : newArray) : undefined,
		}));
	};

	// Multi-select toggle for Content Type
	const handleContentTypeToggle = (value: ReviewContentType) => {
		const current = draftFilters.content_type;
		const currentArray = Array.isArray(current) ? current : current ? [current] : [];

		const newArray = currentArray.includes(value) ? currentArray.filter((v) => v !== value) : [...currentArray, value];

		setDraftFilters((prev) => ({
			...prev,
			content_type: newArray.length > 0 ? (newArray.length === 1 ? newArray[0] : newArray) : undefined,
		}));
	};

	// Helper to check if value is selected in a multi-select filter
	const isSelected = <T extends string>(current: T | T[] | undefined, value: T): boolean => {
		if (!current) return false;
		if (Array.isArray(current)) return current.includes(value);
		return current === value;
	};

	// Apply filters and close popup
	const applyFilters = () => {
		onChange(draftFilters);
		setIsOpen(false);
	};

	// Clear all in draft
	const clearDraftFilters = () => {
		setDraftFilters({});
		setDraftDatePreset("");
	};

	// Remove applied filter (outside popup)
	const removeFilter = (key: keyof ReviewFilters) => {
		const newFilters = { ...filters };
		delete newFilters[key];
		onChange(newFilters);
	};

	// Clear all applied filters (outside popup)
	const clearAllFilters = () => {
		onChange({});
	};

	// Handle quick status filter change (applied immediately)
	const handleQuickStatusChange = (value: ReviewStatus | undefined) => {
		const newFilters = { ...filters };
		if (value) {
			newFilters.status = value;
		} else {
			delete newFilters.status;
		}
		onChange(newFilters);
	};

	// Handle quick type filter change (applied immediately)
	const handleQuickTypeChange = (value: ReviewType | undefined) => {
		const newFilters = { ...filters };
		if (value) {
			newFilters.type = value;
		} else {
			delete newFilters.type;
		}
		onChange(newFilters);
	};

	// Count active filters (excluding status and type which are now quick filters)
	const activeFilters = Object.entries(filters).filter(([key, value]) => {
		if (key === "status") return false; // Handled by quick filter
		if (key === "type") return false; // Handled by quick filter
		if (key === "tags") return Array.isArray(value) && value.length > 0;
		if (key === "tags_mode") return false; // Don't count tags_mode separately
		// Handle array filters (source, content_type)
		if (Array.isArray(value)) return value.length > 0;
		return value !== undefined && value !== "";
	});

	const activeFilterCount = activeFilters.length;

	// Get current status for quick filter (handle array case)
	const currentStatus = Array.isArray(filters.status) ? filters.status[0] : filters.status;

	return (
		<div className={`space-y-3 ${className}`}>
			{/* Row 1: Quick Filters and View Toggle */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					{/* Quick Status Filter */}
					<QuickStatusFilter value={currentStatus} onChange={handleQuickStatusChange} pendingCount={pendingCount} />

					{/* Quick Type Filter (Testimonial/Feedback) */}
					<QuickTypeFilter value={filters.type} onChange={handleQuickTypeChange} />
				</div>
				{children}
			</div>

			{/* Row 2: Filters Button and Active Filter Pills */}
			<div className="flex items-center gap-2 flex-wrap">
				<Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
					<Dialog.Trigger asChild>
						<button
							type="button"
							className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
								activeFilterCount > 0 ? "bg-brand/5 border-brand/20 text-brand hover:bg-brand/10" : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
							}`}
						>
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
								/>
							</svg>
							<span>Filters</span>
							{activeFilterCount > 0 && (
								<span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand text-white text-xs font-semibold">{activeFilterCount}</span>
							)}
						</button>
					</Dialog.Trigger>

					<Dialog.Portal>
						<Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
						<Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] overflow-hidden z-50 flex flex-col">
							{/* Header */}
							<div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
								<Dialog.Title className="text-base font-semibold text-primary">Filters</Dialog.Title>
								<div className="flex items-center gap-2">
									{Object.keys(draftFilters).length > 0 && (
										<button type="button" onClick={clearDraftFilters} className="text-sm text-tertiary hover:text-primary transition-colors">
											Clear all
										</button>
									)}
									<Dialog.Close asChild>
										<button type="button" className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
											</svg>
										</button>
									</Dialog.Close>
								</div>
							</div>

							{/* Filter Sections */}
							<div className="flex-1 overflow-y-auto">
								{/* Content Type / Format (multi-select) */}
								<FilterSection
									title="Format"
									icon={SectionIcons.format}
									isOpen={openSections.has("content_type")}
									isActive={Array.isArray(draftFilters.content_type) ? draftFilters.content_type.length > 0 : !!draftFilters.content_type}
									onToggle={() => toggleSection("content_type")}
								>
									<div className="flex flex-wrap gap-2">
										<ChipOption
											selected={!draftFilters.content_type || (Array.isArray(draftFilters.content_type) && draftFilters.content_type.length === 0)}
											onClick={() => setDraftFilters((prev) => ({ ...prev, content_type: undefined }))}
										>
											All
										</ChipOption>
										{CONTENT_TYPE_OPTIONS.map((option) => (
											<ChipOption key={option.value} selected={isSelected(draftFilters.content_type, option.value)} onClick={() => handleContentTypeToggle(option.value)}>
												{option.label}
											</ChipOption>
										))}
									</div>
								</FilterSection>

								{/* Source (multi-select) */}
								<FilterSection
									title="Source"
									icon={SectionIcons.source}
									isOpen={openSections.has("source")}
									isActive={Array.isArray(draftFilters.source) ? draftFilters.source.length > 0 : !!draftFilters.source}
									onToggle={() => toggleSection("source")}
								>
									<div className="flex flex-wrap gap-2">
										<ChipOption
											selected={!draftFilters.source || (Array.isArray(draftFilters.source) && draftFilters.source.length === 0)}
											onClick={() => setDraftFilters((prev) => ({ ...prev, source: undefined }))}
										>
											All
										</ChipOption>
										{SOURCE_OPTIONS.map((option) => (
											<ChipOption key={option.value} selected={isSelected(draftFilters.source, option.value)} onClick={() => handleSourceToggle(option.value)}>
												{option.label}
											</ChipOption>
										))}
									</div>
								</FilterSection>

								{/* Rating */}
								<FilterSection title="Rating" icon={SectionIcons.rating} isOpen={openSections.has("rating")} isActive={!!draftFilters.rating} onToggle={() => toggleSection("rating")}>
									<div className="flex flex-wrap gap-2">
										{RATING_OPTIONS.map((option) => (
											<ChipOption
												key={option.value}
												selected={draftFilters.rating === option.value || (!draftFilters.rating && option.value === "")}
												onClick={() => handleDraftFilterChange("rating", option.value as ReviewRatingFilter)}
											>
												{option.label}
											</ChipOption>
										))}
									</div>
								</FilterSection>

								{/* Date */}
								<FilterSection title="Date" icon={SectionIcons.date} isOpen={openSections.has("date")} isActive={!!draftFilters.date} onToggle={() => toggleSection("date")}>
									<div className="flex flex-wrap gap-2">
										{DATE_PRESET_OPTIONS.map((option) => (
											<ChipOption key={option.value} selected={draftDatePreset === option.value} onClick={() => handleDraftDatePresetChange(option.value)}>
												{option.label}
											</ChipOption>
										))}
									</div>
								</FilterSection>

								{/* Tags */}
								{tags.length > 0 && (
									<FilterSection
										title="Tags"
										icon={SectionIcons.tags}
										isOpen={openSections.has("tags")}
										isActive={!!draftFilters.tags && draftFilters.tags.length > 0}
										onToggle={() => toggleSection("tags")}
									>
										<div className="space-y-3">
											<div className="flex flex-wrap gap-2">
												{tags.map((tag) => {
													const isTagSelected = draftFilters.tags?.includes(tag.id) || false;
													const colorStyles = getTagColorStyles(tag.color);
													return (
														<button
															key={tag.id}
															type="button"
															onClick={() => handleTagToggle(tag.id)}
															className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all ${colorStyles.bg} ${colorStyles.text} ${
																isTagSelected ? "ring-2 ring-offset-1 ring-gray-800" : "opacity-70 hover:opacity-100"
															}`}
														>
															{tag.label}
														</button>
													);
												})}
											</div>
											{draftFilters.tags && draftFilters.tags.length > 1 && (
												<div className="flex items-center gap-2 pt-2 border-t border-gray-100">
													<span className="text-xs text-tertiary">Match:</span>
													<button
														type="button"
														onClick={() => handleTagsModeChange("any")}
														className={`px-2 py-1 text-xs rounded ${
															draftFilters.tags_mode !== "all" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
														}`}
													>
														Any tag
													</button>
													<button
														type="button"
														onClick={() => handleTagsModeChange("all")}
														className={`px-2 py-1 text-xs rounded ${
															draftFilters.tags_mode === "all" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
														}`}
													>
														All tags
													</button>
												</div>
											)}
										</div>
									</FilterSection>
								)}
							</div>

							{/* Footer */}
							<div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
								<button type="button" onClick={applyFilters} className="w-full px-4 py-2 bg-brand text-white text-sm font-medium rounded-lg hover:bg-brand/90 transition-colors">
									Apply Filters
								</button>
							</div>
						</Dialog.Content>
					</Dialog.Portal>
				</Dialog.Root>

				{/* Active Filter Pills (inline with button) */}
				{activeFilters.map(([key, value]) => (
					<div key={key} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-sm">
						<span className="text-gray-700">{getFilterLabel(key, value as string | string[], tags)}</span>
						<button type="button" onClick={() => removeFilter(key as keyof ReviewFilters)} className="text-gray-400 hover:text-gray-600 transition-colors">
							<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>
				))}

				{/* Clear All */}
				{activeFilterCount > 1 && (
					<button type="button" onClick={clearAllFilters} className="text-sm text-tertiary hover:text-primary transition-colors">
						Clear all
					</button>
				)}

				{/* Review Count - pushed to right */}
				{!isLoading && totalCount !== undefined && totalCount > 0 && (
					<span className="text-sm text-tertiary ml-auto">
						{totalCount} {totalCount === 1 ? "review" : "reviews"}
					</span>
				)}
			</div>
		</div>
	);
}
